import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import aboutBackground from '../assets/about.webp';

import mahesh_kumar from '../assets/provider_headshots/mahesh_kumar.png';
import michelle_garcia from '../assets/provider_headshots/michelle_garcia.png';
import betsy_john_kuruvilla from '../assets/provider_headshots/betsy_john_kuruvilla.jpg';
import raghu_gandhi from '../assets/provider_headshots/raghu_gandhi.jpg';
import liliana_sivak from '../assets/provider_headshots/liliana_sivak.jpeg';

const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';

const services = [
  {
    title: 'Mahesh Kumar, MD',
    description: 'Board Certified Psychiatrist',
    image: mahesh_kumar,
    details: {
      role: 'Board-Certified Psychiatrist',
      education:
        'Dr. Kumar obtained his medical degree from Liaquat Medical College Sindh. He then completed residency in Adult Psychiatry at Hennepin County Medical Center/Regions Hospital Psychiatry Training Program, Twin Cities, Minnesota.',
      experience: [
        'Soon after completing his residency, Dr. Kumar began practicing in the psychiatry department at Regions Hospital in St. Paul, Minnesota, where he worked for over 3 years. During this time, he also provided care at various emergency psychiatric centers across central Minnesota through CentraCare Health Systems.',
        'Dr. Kumar later expanded his practice to Summit Behavioral Health, where he specialized in managing patients with comorbid substance use disorders and mental health conditions. His expertise in treating opioid addiction, particularly through Suboxone maintenance therapy, has been a critical part of his work.',
        'In 2021, Dr. Kumar relocated to San Antonio, Texas, to join Laurel Ridge Treatment Center, one of the largest standalone psychiatric care centers in the world. At Laurel Ridge, he worked in behavioral ICUs, treating patients with complex, chronic mental health conditions, often complicated by polysubstance abuse. In addition to his inpatient work, Dr. Kumar has continued providing outpatient psychiatric care through telehealth services, managing a diverse range of complex psychiatric disorders.',
      ],
    },
  },
  {
    title: 'Raghu Gandhi, MD',
    description: 'Triple Board-Certified Psychiatrist',
    image: raghu_gandhi,
    details: {
      role: 'Triple Board-Certified Psychiatrist (Adult, Child & Adolescent, Addiction Medicine)',
      education:
        'Dr. Gandhi earned his medical degree from the Postgraduate Institute of Medical Sciences in India. He completed his residency in adult psychiatry at Hennepin County Medical Center in Minneapolis, followed by a fellowship in child and adolescent psychiatry at the University of Minnesota.',
      experience: [
        'Dr. Gandhi has served as faculty in the Department of Child and Adolescent Psychiatry at both the University of Minnesota and the University of Texas at Austin, where he contributed to clinical care, teaching, and mentorship.',
        'His clinical expertise includes the evaluation and treatment of autism spectrum disorder, ADHD, anxiety disorders, and mood disorders.',
        'Dr. Gandhi is committed to providing thoughtful, evidence-based, and individualized care to children, adolescents, and adults, with a focus on improving overall functioning and quality of life.',
      ],
    },
  },
  {
    title: 'Betsy Kuruvilla, MSN, PMHNP-BC',
    description: 'Board-Certified Psychiatric Nurse Practitioner',
    image: betsy_john_kuruvilla,
    details: {
      role: 'Board-Certified Psychiatric Mental Health Nurse Practitioner',
      education:
        'Betsy John Kuruvilla earned her Master of Science in Nursing from Walden University, specializing as a Psychiatric Mental Health Nurse Practitioner. Her advanced academic training is anchored by more than 15 years of progressive clinical experience in high-acuity healthcare settings, including critical care, cardiology, and inpatient psychiatry.',
      experience: [
        'Upon earning her board certification as a Psychiatric Mental Health Nurse Practitioner, Betsy transitioned into a specialized role providing comprehensive psychiatric evaluations and evidence-based medication management. She brings a strong depth of clinical expertise and a patient-centered approach to every aspect of her care.',
        'Betsy has experience treating a wide range of mental health conditions across diverse patient populations. Throughout her career, she has been recognized for her clinical leadership, attention to detail, and dedication to patient advocacy.',
        'By combining her extensive frontline medical experience with advanced psychiatric training, Betsy delivers thoughtful, individualized, and compassionate care—supporting patients in achieving long-term mental wellness and stability.',
      ],
    },
  },
  {
    title: 'Michelle Garcia, LCSW',
    description: 'Licensed Clinical Social Worker',
    image: michelle_garcia,
    details: {
      role: 'Licensed Clinical Social Worker',
      education:
        'Michelle obtained her Bachelor of Social Work (BSW) degree from the University of Texas at Austin and Master of Social Work (MSW) degree from the University of Texas at San Antonio. She completed her clinical supervision while working at Laurel Ridge Treatment Center in San Antonio, TX.',
      experience: [
        'Michelle is a native Texan, born and raised in the Rio Grande Valley. After completing her graduate program, she worked as a community case manager with True Mental Health in San Antonio, TX for two years. This is where she began working with individuals with severe mental health issues and diagnoses, learning about social service programs and the systemic challenges that communities face.',
        'Michelle lived in Dallas, TX for two years where she worked for the North Texas Behavioral Health Authority and gained experience in ensuring the quality of care and utilization management of local community mental health clinics.',
        'She later moved back to San Antonio and began working at Laurel Ridge Treatment Center for about three years, which is where she met Dr. Kumar. Michelle worked in the adult inpatient units and provided intensive social work services to diverse individuals with mood, psychotic, and substance use disorders.',
        'Michelle is bilingual in Spanish and also accepts patients whose primary language is Spanish. She offers both in-person and telehealth therapy services at the Aspire BHP clinic.',
      ],
    },
  },
  {
    title: 'Liliana Sivak, LPC-S',
    description: 'Licensed Professional Counselor Supervisor',
    image: liliana_sivak,
    details: {
      role: 'Licensed Professional Counselor Supervisor',
      education:
        'Liliana Sivak graduated in 2019 with a Master of Science degree in Clinical Mental Health Counseling from the University of Texas at San Antonio. She obtained full licensure in 2021 and earned her supervisor designation in 2026. She is currently working toward a Doctorate of Healthcare Administration with Oklahoma State University.',
      experience: [
        'Liliana completed an internship at a domestic violence shelter, where she provided education on abuse, neglect, and resource building.',
        'She has worked in an inpatient psychiatric setting at Laurel Ridge since 2017. She began as a mental health worker assisting with detox management, and after graduating in 2019, transitioned into a therapist position.',
        'As a therapist, Liliana has worked with groups, individuals, and families to support substance use recovery, co-occurring disorders, mood disorders, anxiety, and severe mental illness.',
        'Liliana also has five years of supervisory experience in leadership roles as a lead therapist and clinical program manager for acute inpatient psychiatric care. She has provided mentoring and supervision to interns, non-licensed clinicians, and licensed clinicians, helping with case conceptualization, treatment planning, family education, and discharge planning to support stabilization after hospital stays.',
      ],
    },
  },
];

const About = () => {
  const [open, setOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const handleOpenDialog = (provider) => {
    setSelectedProvider(provider);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedProvider(null);
  };

  return (
    <>
      <Helmet>
        <title>About Us & Providers | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Meet the Aspire Behavioral Health team of psychiatrists, psychiatric nurse practitioners, and therapists providing compassionate mental health care."
        />
      </Helmet>

      {/* Fixed Background for Parallax Effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url(${aboutBackground})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: {
            xs: 'center top',
            sm: 'center center',
            md: 'center center',
          },
          zIndex: -2,
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: {
            xs: 520,
            sm: 580,
            md: 650,
            lg: 620,
          },
          py: {
            xs: 8,
            sm: 10,
            md: 12,
            lg: 10,
          },
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: {
                xs: '2rem',
                sm: '2.5rem',
                md: '3rem',
                lg: '3.5rem',
              },
              lineHeight: 1.2,
            }}
          >
            About Aspire Behavioral Health
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: {
                xs: '1rem',
                sm: '1.1rem',
                md: '1.2rem',
              },
            }}
          >
            Compassionate mental health care from experienced providers who are
            committed to helping you feel heard, supported, and understood.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
              component="a"
              href={zocdocLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                borderColor: 'white',
                color: 'white !important',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:visited': {
                  color: 'white',
                },
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            >
              Schedule an Appointment
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: 8,
          px: { xs: 2, sm: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: '1100px',
            mx: 'auto',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Our Mission
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 7,
              mx: 'auto',
              textAlign: 'center',
              maxWidth: '900px',
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            At Aspire Behavioral Health, we are a dedicated team of mental
            health professionals committed to delivering high-quality care
            through timely access and evidence-based treatment. Our mission is
            to offer comprehensive services that integrate compassionate,
            holistic, and innovative approaches to support better clinical
            outcomes.
            <br />
            <br />
            We believe patients should feel informed, supported, and involved in
            their own treatment plans. By creating personalized goals, we aim to
            improve overall functioning, stability, and quality of life.
          </Typography>

          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Our Providers
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Meet our experienced team of mental health providers offering
              thoughtful, individualized, and compassionate care.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                key={service.title}
                sx={{ display: 'flex' }}
              >
                <Card
                  sx={{
                    width: '100%',
                    minHeight: 250,
                    backgroundColor: '#e3f3c5',
                    color: 'black',
                    borderRadius: 4,
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 2,
                    transition: '0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 6px 18px rgba(0,0,0,0.16)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    <Box
                      sx={{
                        flex: '0 0 120px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          width: 115,
                          height: 115,
                          borderRadius: '20%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          boxShadow: '0px 3px 10px rgba(0,0,0,0.18)',
                        }}
                      />
                    </Box>

                    <CardContent
                      sx={{
                        flex: 1,
                        textAlign: { xs: 'center', sm: 'left' },
                        p: 0,
                        '&:last-child': { pb: 0 },
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          lineHeight: 1.2,
                          mb: 1,
                        }}
                      >
                        {service.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          lineHeight: 1.5,
                          mb: 2,
                          color: '#333',
                        }}
                      >
                        {service.description}
                      </Typography>

                      <Button
                        onClick={() => handleOpenDialog(service)}
                        sx={{
                          backgroundColor: 'white',
                          color: '#3a4c17',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: '25px',
                          textTransform: 'none',
                          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                          },
                        }}
                      >
                        More Info
                      </Button>
                    </CardContent>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Ready to connect with our team?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                href="tel:2108669850"
                sx={{
                  backgroundColor: '#e3f3c5',
                  color: 'black',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#d3eab0',
                  },
                }}
              >
                Call 210-866-9850
              </Button>

              <Button
                component="a"
                href={zocdocLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={{
                  backgroundColor: 'transparent',
                  border: '2px solid #3a4c17',
                  color: '#3a4c17 !important',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:visited': {
                    color: '#3a4c17',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(58,76,23,0.05)',
                    border: '2px solid #3a4c17',
                    color: '#3a4c17',
                  },
                }}
              >
                Schedule an Appointment
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 600 }}>
          {selectedProvider?.title}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" paragraph sx={{ fontWeight: 600 }}>
            {selectedProvider?.details?.role}
          </Typography>

          <Typography variant="body2" paragraph>
            <Box component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Education:
            </Box>
            {selectedProvider?.details?.education}
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Background / Experience:
          </Typography>

          {selectedProvider?.details?.experience?.map((paragraph, index) => (
            <Typography key={index} variant="body2" paragraph>
              {paragraph}
            </Typography>
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default About;